import React from 'react';
import LegalPage from './LegalPage-new';

function TermsOfServicePage(): React.ReactElement
{
    return (
        <LegalPage
            eyebrow="Terms of Service"
            title="Rules for using AcceliQA responsibly"
            summary="These terms govern access to AcceliQA, including account creation, repository uploads, generated test assets, and any integrations you enable through the platform."
            lastUpdated="April 3, 2026"
            sections={[
                {
                    id: 'acceptance',
                    title: 'Acceptance of Terms',
                    content: (
                        <>
                            <p>
                                By accessing or using AcceliQA, you agree to be bound by these Terms of Service and any policies referenced here. If you are using the service on behalf of a company or other legal entity, you represent that you have authority to bind that entity.
                            </p>
                            <p>
                                If you do not agree to these terms, do not access the platform, upload content, or connect external systems.
                            </p>
                        </>
                    )
                },
                {
                    id: 'accounts',
                    title: 'Accounts and Access',
                    content: (
                        <>
                            <p>
                                You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Account sharing is not permitted unless your subscription expressly allows shared or delegated access.
                            </p>
                            <ul>
                                <li>Provide accurate registration and billing information.</li>
                                <li>Promptly update user roles and revoke access for departing team members.</li>
                                <li>Notify us immediately if you suspect unauthorized access or credential compromise.</li>
                            </ul>
                        </>
                    )
                },
                {
                    id: 'permitted-use',
                    title: 'Permitted Use',
                    content: (
                        <>
                            <p>
                                You may use AcceliQA to analyze repositories, requirements, and project artifacts for the purpose of generating software tests, coverage insights, and related engineering outputs. You must only upload or connect content that you are authorized to use.
                            </p>
                            <ul>
                                <li>No unlawful, infringing, or deceptive use of the service.</li>
                                <li>No attempts to interfere with service availability, rate limits, or platform security controls.</li>
                                <li>No reverse engineering of the service except where applicable law expressly permits it.</li>
                            </ul>
                        </>
                    )
                },
                {
                    id: 'customer-content',
                    title: 'Customer Content and Generated Output',
                    content: (
                        <>
                            <p>
                                You retain ownership of code, requirements, documents, prompts, and other materials you submit to AcceliQA. Subject to these terms, you grant us a limited license to host, process, transmit, and analyze that content solely to operate, secure, and improve the service for your organization.
                            </p>
                            <p>
                                Generated test cases, coverage reports, and related outputs are provided for your internal evaluation and development workflows. You are responsible for validating generated output before relying on it in production or regulated environments.
                            </p>
                        </>
                    )
                },
                {
                    id: 'subscriptions',
                    title: 'Subscriptions, Billing, and Suspension',
                    content: (
                        <>
                            <p>
                                Paid features may require an active subscription. Fees, usage limits, and billing cycles are governed by your order form or subscription plan. Failure to pay applicable fees may result in suspension or reduction of access after reasonable notice.
                            </p>
                            <p>
                                We may suspend access immediately if necessary to address security threats, fraudulent activity, abuse of the platform, or legal obligations.
                            </p>
                        </>
                    )
                },
                {
                    id: 'disclaimers',
                    title: 'Disclaimers and Limitation of Liability',
                    content: (
                        <>
                            <p>
                                AcceliQA is provided on an "as is" and "as available" basis. While we aim for reliable service and useful generated output, we do not guarantee that the platform will be uninterrupted, error-free, or suitable for every use case.
                            </p>
                            <p>
                                To the maximum extent permitted by law, Accelirate will not be liable for indirect, incidental, consequential, special, or punitive damages, or for loss of profits, revenues, data, or goodwill arising out of or related to your use of the service.
                            </p>
                        </>
                    )
                },
                {
                    id: 'termination',
                    title: 'Termination',
                    content: (
                        <>
                            <p>
                                You may stop using the service at any time. We may terminate or suspend your access if you materially breach these terms, fail to cure a violation after notice where applicable, or if continued service would create legal or security risk.
                            </p>
                            <p>
                                Provisions relating to ownership, confidentiality, disclaimers, limitations of liability, and dispute resolution will survive termination.
                            </p>
                        </>
                    )
                }
            ]}
            footerNote="Questions about these terms can be sent to contact@accelirate.com. If you require contractual or procurement-specific language, use your order form or master services agreement as the controlling document where applicable."
        />
    );
}

export default TermsOfServicePage;